import { RefreshCwIcon } from "lucide-react"
import { useEffect } from "react"
import { toast } from "sonner"
import { noticeOf } from "./update-policy.ts"
import { putNoticeAway, reloadToUpdate, useUpdate } from "./updates.ts"

/*
 * The notice that a new release of Beacon is ready, or that this tab is behind one: a toast that
 * stays until it is answered, with Reload and Later. Later (or a swipe) puts it away until there is
 * something new to say; the avatar's dot and "Update Beacon" in the avatar's drawer and the account
 * menu stay, so the update is never out of reach. Reload over unsaved work keeps the notice up and
 * says to save or discard it first. While a page says Beacon has been updated in its own place, or
 * the avatar's drawer is open with Update Beacon at its head, the notice leaves it to them (it would
 * cover the drawer's rows). `main.tsx` renders it once, beside the Toaster.
 */

/** The notice's one toast, replaced in place as what it says changes. */
const noticeId = "beacon-update"

/**
 * Which showing of the notice a dismissal belongs to. Sonner reports a toast taken down in code as
 * dismissed too, and only the Member's own dismissal is Later.
 */
let showing = 0

export function UpdateNotice() {
  const { state, later, saidElsewhere } = useUpdate()
  const notice = noticeOf(state)
  const leftElsewhere = saidElsewhere && !(state.status !== "current" && state.held)
  const shown = notice && !later && !leftElsewhere ? notice : undefined
  const title = shown?.title
  const description = shown?.description

  useEffect(() => {
    showing += 1
    if (!title) {
      toast.dismiss(noticeId)
      return
    }
    const mine = showing
    toast(title, {
      id: noticeId,
      description,
      duration: Infinity,
      icon: <RefreshCwIcon aria-hidden className="size-4 text-brand-text" />,
      action: {
        label: "Reload",
        onClick: (event) => {
          // The notice stays up until the page reloads, or to say why it didn't.
          event.preventDefault()
          reloadToUpdate()
        },
      },
      cancel: { label: "Later", onClick: putNoticeAway },
      onDismiss: () => {
        if (mine === showing) putNoticeAway()
      },
    })
  }, [title, description])

  return null
}
